import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";
import type { webSourceIdSelectedAction } from "../../shared/store/action-creators";
import type { AccountOverviewCompanyState } from "../types/state";
import * as ac from "./action-creators";
import initialState from "./initial-state";

const accountOverviewCompanyReducer = createReducer<
    AccountOverviewCompanyState,
    ActionType<typeof ac | typeof webSourceIdSelectedAction>
>(initialState)
    .handleAction(
        ac.tldSitesIncludeChangedAction,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            areAllTLDSitesIncluded: payload,
            geoBreakdownTablePageSize: initialState.geoBreakdownTablePageSize,
        }),
    )
    .handleAction(
        ac.geoBreakdownTablePageSizeChangedAction,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            geoBreakdownTablePageSize: payload,
        }),
    )
    .handleAction(
        ac.geoBreakdownTableResultsClearedAction,
        (state): AccountOverviewCompanyState => ({
            ...state,
            geoBreakdownTableData: initialState.geoBreakdownTableData,
        }),
    )
    .handleAction(
        ac.visitsOverTimeGranularityChangedAction,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            selectedTimeGranularity: payload,
        }),
    )
    .handleAction(
        ac.visitsOverTimeMonthToDateChangedAction,
        (state): AccountOverviewCompanyState => ({
            ...state,
            isMonthToDateActive: !state.isMonthToDateActive,
        }),
    )
    .handleAction(
        ac.setCompanyEventFilterAction,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            selectedEventFilter: payload,
        }),
    )
    .handleAction(
        ac.setCompanyFilteredSignalEventsAction,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            filteredSignalEvents: payload,
        }),
    )
    // Visits geo breakdown table data
    .handleAction(
        ac.fetchGeoBreakdownTableDataAsync.request,
        (state): AccountOverviewCompanyState => ({
            ...state,
            geoBreakdownTableDataFetchError: initialState.geoBreakdownTableDataFetchError,
            isGEOBreakdownTableDataBeingFetched: true,
        }),
    )
    .handleAction(
        ac.fetchGeoBreakdownTableDataAsync.success,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            geoBreakdownTableData: payload,
            isGEOBreakdownTableDataBeingFetched: false,
        }),
    )
    .handleAction(
        ac.fetchGeoBreakdownTableDataAsync.failure,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            geoBreakdownTableDataFetchError: payload,
            isGEOBreakdownTableDataBeingFetched: false,
        }),
    )
    .handleAction(
        ac.fetchSignalEventsAsync.request,
        (state): AccountOverviewCompanyState => ({
            ...state,
            signalEvents: [],
            signalEventsFetchError: initialState.signalEventsFetchError,
            isSignalEventsBeingFetched: true,
            isSignalEventsFetched: false,
        }),
    )
    .handleAction(
        ac.fetchSignalEventsAsync.success,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            signalEvents: payload,
            isSignalEventsBeingFetched: false,
            isSignalEventsFetched: true,
        }),
    )
    .handleAction(
        ac.fetchSignalEventsAsync.failure,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            signalEvents: [],
            signalEventsFetchError: payload,
            isSignalEventsBeingFetched: false,
            isSignalEventsFetched: false,
        }),
    )
    .handleAction(
        ac.fetchOtherSignalEventsAsync.request,
        (state): AccountOverviewCompanyState => ({
            ...state,
            otherSignalEvents: [],
            otherSignalEventsFetchError: initialState.otherSignalEventsFetchError,
            isOtherSignalEventsBeingFetched: true,
        }),
    )
    .handleAction(
        ac.fetchOtherSignalEventsAsync.success,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            otherSignalEvents: payload,
            isOtherSignalEventsBeingFetched: false,
        }),
    )
    .handleAction(
        ac.fetchOtherSignalEventsAsync.failure,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            otherSignalEvents: [],
            otherSignalEventsFetchError: payload,
            isOtherSignalEventsBeingFetched: false,
        }),
    )
    .handleAction(
        ac.toggleCompaniesSelectionAction,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            isCompanySelectionEnabled: payload,
        }),
    )
    .handleAction(
        ac.setSelectedCompanyDomainsAction,
        (state, { payload }): AccountOverviewCompanyState => ({
            ...state,
            selectedCompanyDomains: payload,
        }),
    );

export default accountOverviewCompanyReducer;
